import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface ConversationShare {
  id: string;
  conversation_id: string;
  share_token: string;
  snapshot_at: string;
  revoked_at: string | null;
  created_at: string;
}

export function useConversationShare(conversationId: string | null | undefined) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['conversation-share', conversationId],
    enabled: !!conversationId && !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation_shares')
        .select('*')
        .eq('conversation_id', conversationId!)
        .eq('owner_id', user!.id)
        .is('revoked_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as ConversationShare) ?? null;
    },
  });

  const createShare = useMutation({
    mutationFn: async () => {
      if (!conversationId || !user?.id) throw new Error('Sem conversa');
      const { data, error } = await supabase
        .from('conversation_shares')
        .insert({ conversation_id: conversationId, owner_id: user.id })
        .select('*')
        .single();
      if (error) throw error;
      return data as ConversationShare;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversation-share', conversationId] }),
  });

  const refreshSnapshot = useMutation({
    mutationFn: async (shareId: string) => {
      const { error } = await supabase
        .from('conversation_shares')
        .update({ snapshot_at: new Date().toISOString() })
        .eq('id', shareId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversation-share', conversationId] }),
  });

  const revokeShare = useMutation({
    mutationFn: async (shareId: string) => {
      const { error } = await supabase
        .from('conversation_shares')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', shareId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversation-share', conversationId] }),
  });

  return { share: query.data, isLoading: query.isLoading, createShare, refreshSnapshot, revokeShare };
}

export function buildShareUrl(token: string) {
  return `${window.location.origin}/share/${token}`;
}
